import { createFileRoute } from '@tanstack/react-router';
import { Grid, Group, Stack, Text } from '@mantine/core';
import { Activity, AlertTriangle, GaugeCircle, Server } from 'lucide-react';
import { Suspense, lazy, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Badge, Card, Skeleton, StatusDot } from '../components/ui';
import { PageContainer, PageHeader, Section } from '../components/ui';
import { staggerContainer, rowItem } from '../lib/motion';
import { useRanges } from '../hooks/useRanges';
import { AlertFeed, type AlertItem } from '../components/monitoring/AlertFeed';
import { colors } from '../theme/tokens';
import type { HealthSeriesPoint } from '../components/monitoring/HealthDashboard';

const HealthDashboard = lazy(() => import('../components/monitoring/HealthDashboard'));

export const Route = createFileRoute('/range-health')({
  component: RangeHealth,
});

function RangeHealth() {
  const { data, isPending } = useRanges();

  // Fleet utilization series: empty until we wire CloudWatch Metrics
  // aggregation across active ranges. The previous version of this route
  // generated Math.random() points which was misleading (looked like a live
  // feed but was pure noise). Operator rule 2026-05-25: no mock data on any
  // page. Backend endpoint to render real data: GET /monitor/ranges/{id}/health
  // returns per-range VM SSM-ping status; a future fleet-wide aggregate is
  // tracked in [[project-forge-v2-status]] under Phase 5 / observability.
  const fleetSeries = useMemo<ReadonlyArray<HealthSeriesPoint>>(() => [], []);

  // Alert feed: empty until the Wazuh manager -> Elastic -> CloudWatch alarm
  // pipe lands. The forge-router-configure SSM Doc already wires fw-drop
  // events to the per-range Wazuh manager; the next step is a Wazuh -> EventBridge
  // bridge + a forge-alerts DynamoDB table the frontend queries from.
  const alerts: ReadonlyArray<AlertItem> = useMemo(() => [], []);
  const totalVms = data?.reduce((acc, r) => acc + r.vmCount, 0) ?? 0;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Activity}
        title="Range Health"
        description="Provisioning, agent connectivity and detection-pipeline health for every range."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Range Health' }]}
      />

      <Section
        title="Fleet overview"
        description="Per-range VM health, Wazuh agent connectivity, and live utilization. Auto-refreshes every 15 seconds."
      >
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card>
              <Stack gap="md">
                <Group gap="xs">
                  <Activity size={16} color={colors.hudCyan} />
                  <Text fw={600} size="sm">
                    Fleet utilization
                  </Text>
                </Group>
                {isPending ? (
                  <Skeleton height={280} />
                ) : fleetSeries.length === 0 ? (
                  <Stack
                    align="center"
                    justify="center"
                    gap="xs"
                    style={{
                      height: 280,
                      background: 'rgb(var(--c-deep))',
                      border: '1px dashed var(--c-bd)',
                      borderRadius: 8,
                    }}
                  >
                    <Activity size={28} color="#52525b" />
                    <Text size="sm" c="dimmed" ta="center">
                      Fleet-wide utilization charts wire up once
                      <br />
                      the Wazuh / Elastic / CloudWatch metrics pipe lands.
                    </Text>
                    <Text size="xs" c="dimmed" ta="center" maw={420}>
                      Per-range VM health is already available via{' '}
                      <code className="font-mono">
                        GET /monitor/ranges/{`{id}`}/health
                      </code>{' '}
                      — open any range to see live SSM ping + EC2 status.
                    </Text>
                    <Badge variant="warn" size="xs">
                      {totalVms} VM{totalVms === 1 ? '' : 's'} across {data?.length ?? 0} range
                      {data?.length === 1 ? '' : 's'}
                    </Badge>
                  </Stack>
                ) : (
                  <Suspense fallback={<Skeleton height={280} />}>
                    <HealthDashboard series={fleetSeries} vmTotal={totalVms} />
                  </Suspense>
                )}
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card>
              <Stack gap="md">
                <Group gap="xs">
                  <GaugeCircle size={16} color={colors.neon} />
                  <Text fw={600} size="sm">
                    Range roster
                  </Text>
                </Group>
                {isPending ? (
                  <Skeleton lines={3} height={20} />
                ) : (
                  <motion.div variants={staggerContainer} initial="hidden" animate="show">
                    <Stack gap={6}>
                      {data?.map((r) => (
                        <motion.div key={r.id} variants={rowItem}>
                          <Group justify="space-between">
                            <Group gap="xs">
                              <StatusDot
                                state={r.status === 'attacking' ? 'warning' : r.status === 'failed' ? 'error' : 'running'}
                                size="sm"
                              />
                              <Text size="sm" className="font-mono" c="rgb(var(--c-txt-bright))">
                                {r.id}
                              </Text>
                            </Group>
                            <Group gap="xs">
                              <Server size={12} color="#71717a" />
                              <Text size="xs" c="dimmed" className="font-mono">
                                {r.vmCount}
                              </Text>
                            </Group>
                          </Group>
                        </motion.div>
                      ))}
                    </Stack>
                  </motion.div>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Section>

      <Section
        title="Live alert feed"
        description="Detections from Wazuh manager rule matches and CloudWatch alarms."
        action={<Badge variant="info">last 24h</Badge>}
      >
        <Card>
          <Stack gap="md">
            <Group gap="xs">
              <AlertTriangle size={16} color={colors.hudAmber} />
              <Text fw={600} size="sm">
                Live alert feed
              </Text>
            </Group>
            {alerts.length === 0 ? (
              <Stack
                align="center"
                justify="center"
                gap={6}
                style={{
                  padding: '32px 16px',
                  background: 'rgb(var(--c-deep))',
                  border: '1px dashed var(--c-bd)',
                  borderRadius: 8,
                }}
              >
                <AlertTriangle size={24} color="#52525b" />
                <Text size="sm" c="dimmed" ta="center">
                  No alerts yet.
                </Text>
                <Text size="xs" c="dimmed" ta="center" maw={520}>
                  Live alerts flow in from Wazuh manager (rule matches) and CloudWatch alarms
                  (range-level metrics). The bridge between Wazuh and EventBridge is the next
                  wiring step — when a range deploys with the Wazuh AMI, its alerts will appear here.
                </Text>
              </Stack>
            ) : (
              <AlertFeed items={alerts} />
            )}
          </Stack>
        </Card>
      </Section>
    </PageContainer>
  );
}
